import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { AuthLayout } from '@/components/auth-layout';
import { FormError } from '@/components/form-error';
import { FormField } from '@/components/form-field';
import { authClient } from '@/lib/auth-client';
import { authErrorMessage } from '@/lib/auth-errors';
import { isInvitationPath, nextQuery, safeNextPath } from '@/lib/next-path';
import { validateAuthForm, type FieldErrors } from '@/lib/validation';
import { InvitationLoginHint } from '@/routes/invitation';

export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNextPath(params.get('volgende'));
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [pending, setPending] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const values = { email: String(form.get('email')), password: String(form.get('password')) };
    const errors = validateAuthForm(values, 'login');
    setFieldErrors(errors);
    setError(null);
    if (Object.keys(errors).length > 0) return;

    setPending(true);
    const { error } = await authClient.signIn.email(values);
    setPending(false);
    if (error) return setError(authErrorMessage(error));
    void navigate(next ?? '/', { replace: true });
  }

  return (
    <AuthLayout title="Inloggen" description="Welkom terug. Je trainingen staan klaar.">
      <form onSubmit={onSubmit} noValidate className="grid gap-6">
        {isInvitationPath(next) && <InvitationLoginHint />}
        <FormError message={error} />
        <div className="grid gap-5">
          <FormField id="email" label="E-mailadres" error={fieldErrors.email}>
            <Input name="email" type="email" autoComplete="email" placeholder="naam@club.nl" />
          </FormField>
          <FormField id="password" label="Wachtwoord" error={fieldErrors.password}>
            <Input name="password" type="password" autoComplete="current-password" />
          </FormField>
        </div>
        <Button type="submit" size="lg" loading={pending}>
          Inloggen
        </Button>
        <Separator />
        <p className="text-center text-sm text-muted-foreground">
          Nog geen account?{' '}
          <Link
            to={`/registreren${nextQuery(next)}`}
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            Account aanmaken
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
